import { useEffect } from 'react'
import { ChevronRight, ExternalLink, Building2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cleanBrDescription, parseDescriptionBullets } from '../../lib/utils'
import type { Product, Variant, ProductCard, DistributorOffer } from '../../types'
import { useAuth } from '../../context/AuthContext'
import { trackEvent } from '../../lib/analytics'
import ProductDetailHero from '../../components/product/ProductDetailHero'
import ProductAttributes from '../../components/product/ProductAttributes'
import ProductDocuments from '../../components/product/ProductDocuments'
import ProductRelated from '../../components/product/ProductRelated'
import ProductDistributorOffers from '../../components/product/ProductDistributorOffers'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs'
import { canonicalPath } from './seo'
import SsrVariantChips from './SsrVariantChips'
import ProductRelationsSection from './ProductRelationsSection'
import SuccessorBanner from './SuccessorBanner'
import AccessoryBundle from '../../components/product/AccessoryBundle'

export interface ProductDetailViewProps {
  lang: string
  product: Product
  variants: Variant[]
  selected: Variant | null
  related?: ProductCard[]
  distributorOffers?: DistributorOffer[]
}

/**
 * The product detail page. Renders the same component tree on SSR and CSR.
 * Assumed to live inside the shop-chrome Layout (header/footer/providers/
 * router). Uses `<a>`-based variant chips so changing variant triggers a
 * full navigation — each variant URL is its own crawlable SSR page.
 *
 * Interactive pieces that touch the browser (useCart / localStorage / toast)
 * are SSR-guarded in their respective modules, so the server render matches
 * the first client render and hydration is clean.
 */
export default function ProductDetailView({
  lang,
  product,
  variants,
  selected,
  related,
  distributorOffers,
}: ProductDetailViewProps) {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const displayMpn = selected?.normalized_mpn ?? product.normalized_mpn

  useEffect(() => {
    trackEvent('Product View', { sku: displayMpn, name: product.display_title ?? displayMpn })
  }, [displayMpn, product.display_title])
  const displayTitle = selected?.display_title ?? product.display_title ?? product.normalized_mpn
  const productPath = canonicalPath(lang, product.slug)

  // Only offer distributor tab when we have at least one offer or distributor URLs.
  const hasDistributorData =
    (distributorOffers && distributorOffers.length > 0) ||
    Boolean(product.product_url_rs || product.product_url_distrelec || product.rs_stock_number || product.distrelec_stock_number)

  const hasAccessories = (product.accessories?.length ?? 0) > 0
  const hasDocuments =
    (product.downloadable_assets?.length ?? 0) > 0 ||
    (product.datasheet_urls?.length ?? 0) > 0 ||
    (product.manual_urls?.length ?? 0) > 0 ||
    (product.brochure_urls?.length ?? 0) > 0

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <Breadcrumb
        lang={lang}
        category={product.category}
        mpn={displayMpn}
        displayTitle={displayTitle}
      />

      <ProductDetailHero product={product} variant={selected ?? undefined} variants={variants} />

      {/* T4.3: discontinued → successor (with migration note + contact), self-hides when none */}
      <SuccessorBanner lang={lang} relations={product.relations} />

      {variants.length > 1 ? (
        <SsrVariantChips
          lang={lang}
          product={product}
          variants={variants}
          selected={selected}
        />
      ) : null}

      {!isAuthenticated && (
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-primary/20 bg-neutral-50 px-4 py-3">
          <Building2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-neutral-700">{t('shop.b2b.pdpBanner')}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <a
              href={`${productPath}/offerte`}
              className="text-sm font-medium text-primary hover:underline"
            >
              {t('shop.b2b.pdpRequestQuote')}
            </a>
            <a
              href={`/${encodeURIComponent(lang)}/login`}
              className="text-sm font-medium text-neutral-600 hover:text-primary"
            >
              {t('shop.b2b.pdpLogin')}
            </a>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mt-2">
        <a
          href={`${productPath}/offerte`}
          className="inline-block text-sm text-primary hover:underline"
        >
          {t('cta.requestQuote')}
        </a>
        {product.product_url_br && (
          <a
            href={product.product_url_br}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 border border-neutral-200 rounded-md px-3 py-1.5 text-sm text-neutral-600 hover:border-primary hover:text-primary transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {t('product.brProductInfo')}
          </a>
        )}
      </div>

      {/* T4.3: "Dit heeft u ook nodig" bundle — self-hides when no orderable accessories */}
      <AccessoryBundle product={product} />

      <section className="mt-10">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">{t('product.overview')}</TabsTrigger>
            <TabsTrigger value="specifications">{t('product.specifications')}</TabsTrigger>
            {hasDocuments ? <TabsTrigger value="documents">{t('product.documents')}</TabsTrigger> : null}
            {hasAccessories ? <TabsTrigger value="accessories">{t('product.accessories')}</TabsTrigger> : null}
            {hasDistributorData ? (
              <TabsTrigger value="distributors">{t('product.distributors')}</TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab product={product} />
          </TabsContent>

          <TabsContent value="specifications">
            <ProductAttributes
              technical_attributes={product.technical_attributes ?? []}
              filterable_attributes={product.filterable_attributes ?? {}}
              compliance_attributes={
                Array.isArray(product.compliance_attributes)
                  ? {}
                  : (product.compliance_attributes ?? {})
              }
            />
          </TabsContent>

          {hasDocuments ? (
            <TabsContent value="documents">
              <ProductDocuments
                downloadable_assets={product.downloadable_assets ?? []}
                datasheetUrls={product.datasheet_urls ?? []}
                manualUrls={product.manual_urls ?? []}
                brochureUrls={product.brochure_urls ?? []}
              />
            </TabsContent>
          ) : null}

          {hasAccessories ? (
            <TabsContent value="accessories">
              <AccessoriesTab product={product} />
            </TabsContent>
          ) : null}

          {hasDistributorData ? (
            <TabsContent value="distributors">
              <ProductDistributorOffers product={product} />
            </TabsContent>
          ) : null}
        </Tabs>
      </section>

      {/* STORY-003: explicit relations (replaces / compatible / related) above the
          similarity-derived related-products rail. Null-safe — renders nothing
          when the API didn't return the field. */}
      <ProductRelationsSection lang={lang} relations={product.relations} />

      <ProductRelated
        series={product.series}
        category={product.category}
        currentSlug={product.slug}
        serverRelated={related}
      />
    </div>
  )
}

function Breadcrumb({
  lang,
  category,
  mpn,
  displayTitle,
}: {
  lang: string
  category: string
  mpn: string
  displayTitle: string
}) {
  const { t } = useTranslation()
  const label = displayTitle !== mpn ? displayTitle : mpn
  const crumb = label.length > 60 ? label.slice(0, 57) + '…' : label
  return (
    <nav aria-label="Breadcrumb" className="text-sm text-neutral-600 mb-4">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <a href={`/${encodeURIComponent(lang)}`} className="hover:text-primary">
            {t('nav.home')}
          </a>
        </li>
        <li aria-hidden="true">
          <ChevronRight className="h-3 w-3 inline-block" />
        </li>
        <li>
          <a href={`/${encodeURIComponent(lang)}/products`} className="hover:text-primary">
            {t('nav.products')}
          </a>
        </li>
        {category ? (
          <>
            <li aria-hidden="true">
              <ChevronRight className="h-3 w-3 inline-block" />
            </li>
            <li>
              <a
                href={`/${encodeURIComponent(lang)}/products?category=${encodeURIComponent(category)}`}
                className="hover:text-primary"
              >
                {category}
              </a>
            </li>
          </>
        ) : null}
        <li aria-hidden="true">
          <ChevronRight className="h-3 w-3 inline-block" />
        </li>
        <li className="text-neutral-700 font-medium" aria-current="page">
          {crumb}
        </li>
      </ol>
    </nav>
  )
}

function DescriptionText({ text, testId }: { text: string; testId?: string }) {
  const bullets = parseDescriptionBullets(text)
  if (bullets.length > 0) {
    return (
      <ul className="list-disc list-inside space-y-1 text-neutral-700 text-sm leading-relaxed" data-testid={testId}>
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    )
  }
  return <p className="text-neutral-700 leading-relaxed text-sm" data-testid={testId}>{text}</p>
}

function OverviewTab({ product }: { product: Product }) {
  const hasSpecs =
    (product.certifications?.length ?? 0) > 0 ||
    product.ip_rating ||
    product.operating_temp_min != null ||
    product.operating_temp_max != null ||
    product.power_consumption_bus_w != null ||
    product.power_consumption_io_w != null ||
    product.country_of_origin ||
    product.net_weight_g != null ||
    product.series ||
    product.product_family ||
    product.br_id_code

  const shortDesc = cleanBrDescription(product.short_description)
  const longDesc = cleanBrDescription(product.long_description)
  // Don't show long description when it's identical to or a substring of the short description.
  const showLong = longDesc && longDesc !== shortDesc && !shortDesc.includes(longDesc)

  return (
    <div className="flex flex-col gap-6">
      {shortDesc ? (
        <section className="prose max-w-none">
          <h3 className="text-base font-semibold text-neutral-900 mb-2">Omschrijving</h3>
          <DescriptionText text={shortDesc} testId="product-short-description" />
          {showLong ? (
            <div className="mt-3">
              <DescriptionText text={longDesc} />
            </div>
          ) : null}
        </section>
      ) : null}

      {hasSpecs ? (
        <section className="border-t border-neutral-100 pt-6">
          <h3 className="text-base font-semibold text-neutral-900 mb-3">Productinformatie</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {product.series ? <InfoRow label="Serie" value={product.series} /> : null}
            {product.product_family ? (
              <InfoRow label="Productfamilie" value={product.product_family} />
            ) : null}
            {product.ip_rating ? <InfoRow label="IP-klasse" value={product.ip_rating} /> : null}
            {product.operating_temp_min != null || product.operating_temp_max != null ? (
              <InfoRow
                label="Bedrijfstemperatuur"
                value={formatTempRange(
                  product.operating_temp_min,
                  product.operating_temp_max,
                )}
              />
            ) : null}
            {product.power_consumption_bus_w != null ? (
              <InfoRow
                label="Vermogen (bus)"
                value={`${product.power_consumption_bus_w} W`}
              />
            ) : null}
            {product.power_consumption_io_w != null ? (
              <InfoRow
                label="Vermogen (I/O)"
                value={`${product.power_consumption_io_w} W`}
              />
            ) : null}
            {product.country_of_origin ? (
              <InfoRow label="Land van herkomst" value={product.country_of_origin} />
            ) : null}
            {product.net_weight_g != null ? (
              <InfoRow label="Nettogewicht" value={`${product.net_weight_g} g`} />
            ) : null}
            {product.br_id_code ? (
              <InfoRow label="B&R ID-code" value={product.br_id_code} />
            ) : null}
          </dl>
        </section>
      ) : null}

      {product.certifications && product.certifications.length > 0 ? (
        <section className="border-t border-neutral-100 pt-6">
          <h3 className="text-base font-semibold text-neutral-900 mb-3">Certificeringen</h3>
          <div className="flex flex-wrap gap-2">
            {product.certifications.map((cert) => (
              <span
                key={cert}
                className="inline-flex items-center rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-700"
              >
                {cert}
              </span>
            ))}
          </div>
        </section>
      ) : null}

    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-neutral-50 rounded-lg p-3">
      <dt className="text-xs text-neutral-600 mb-0.5">{label}</dt>
      <dd className="text-sm font-medium text-neutral-900">{value}</dd>
    </div>
  )
}

function formatTempRange(min: number | null, max: number | null): string {
  if (min != null && max != null) return `${min}°C tot ${max}°C`
  if (min != null) return `vanaf ${min}°C`
  if (max != null) return `tot ${max}°C`
  return ''
}

function AccessoriesTab({ product }: { product: Product }) {
  const accessories = product.accessories ?? []
  if (accessories.length === 0) {
    return (
      <p className="text-sm text-neutral-600 italic">
        Geen accessoires beschikbaar voor dit product.
      </p>
    )
  }
  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b border-neutral-200">
          <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">
            MPN
          </th>
          <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">
            Omschrijving
          </th>
          <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">
            Categorie
          </th>
        </tr>
      </thead>
      <tbody>
        {accessories.map((a, i) => (
          <tr key={`${a.mpn}-${i}`} className="border-b border-neutral-100">
            <td className="py-2 px-3 font-mono text-xs text-neutral-700">{a.mpn}</td>
            <td className="py-2 px-3 text-neutral-900">{a.description}</td>
            <td className="py-2 px-3 text-neutral-600 text-xs">{a.category || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
